'use client';

import {
  CmsMasonryImageGrid,
  type CmsMasonryImageItem,
} from '@/cms/components/blocks/shared/cms-masonry-image-grid.client';
import { runWhenPageReadyAndIdle } from '@/lib/ui/run-when-idle';
import { cn } from '@/lib/ui/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CmsWorkspaceShellModel } from '../contracts/server-model';
import { WORKSPACE_MOBILE_MEDIA_QUERY } from '../shell/workspace-viewport';
import {
  WORKSPACE_PREVIEW_MASONRY_CLASS,
  WORKSPACE_PREVIEW_MASONRY_COLUMN_CLASS,
  WORKSPACE_PREVIEW_MASONRY_ITEM_CLASS,
  WORKSPACE_PREVIEW_MASONRY_SINGLE_CLASS,
  WORKSPACE_PREVIEW_MASONRY_WRAP_CLASS,
  type WorkspacePreviewImage,
  distributeWorkspacePreviewImages,
  shuffleWorkspacePreviewImages,
} from './workspace-preview-layout';
import {
  WORKSPACE_PREVIEW_BATCH_SIZE,
  WORKSPACE_PREVIEW_IMAGE_VARIANT,
  WORKSPACE_PREVIEW_INITIAL_COUNT,
  resolveWorkspacePreviewImageSizes,
} from './workspace-preview-media';
import { WorkspacePreviewSkeletonGrid } from './workspace-preview-skeleton';

const WORKSPACE_CANVAS_SCROLL_ROOT_SELECTOR =
  '[data-workspace-canvas-scroll-root]';
const WORKSPACE_PREVIEW_WIDE_MEDIA_QUERY = '(min-width: 80rem)';

type WorkspacePreviewLayout =
  | { viewport: 'pending'; columnCount: 4 }
  | { viewport: 'desktop'; columnCount: 3 | 4 }
  | { viewport: 'mobile'; columnCount: 3 };

function WorkspacePreviewImageGrid({
  images,
  columnCount,
  sizes,
  onImageSettled,
}: {
  images: WorkspacePreviewImage[];
  columnCount: number;
  sizes: string;
  onImageSettled: (image: CmsMasonryImageItem) => void;
}) {
  const keyedImages = images.map((image, index) => ({
    ...image,
    key: `workspace-preview-${index}-${image.src}`,
  }));
  const columns = distributeWorkspacePreviewImages(keyedImages, columnCount);

  return (
    <div
      className={cn('cms-masonry-image-grid', WORKSPACE_PREVIEW_MASONRY_CLASS)}
      data-column-count={columnCount}
    >
      {columns.map((column, columnIndex) => (
        <div
          key={`workspace-preview-column-${columnIndex}`}
          className={WORKSPACE_PREVIEW_MASONRY_COLUMN_CLASS}
          data-workspace-preview-column={columnIndex}
        >
          <CmsMasonryImageGrid
            images={column}
            sizes={sizes}
            variant={WORKSPACE_PREVIEW_IMAGE_VARIANT}
            fetchPriority="low"
            itemClassName={WORKSPACE_PREVIEW_MASONRY_ITEM_CLASS}
            imageClassName="cms-block-workspace-workspace-1-workspace-showcase-layout__workspacePreviewMasonryImage"
            onImageSettled={onImageSettled}
          />
        </div>
      ))}
    </div>
  );
}

function WorkspacePreviewMasonryList({
  images,
}: {
  images: CmsWorkspaceShellModel['hero']['previewImages'];
}) {
  const [layout, setLayout] = useState<WorkspacePreviewLayout>({
    viewport: 'pending',
    columnCount: 4,
  });
  const [orderedImages, setOrderedImages] = useState(images);
  const [visibleCount, setVisibleCount] = useState(
    WORKSPACE_PREVIEW_INITIAL_COUNT
  );
  const [canLoadImages, setCanLoadImages] = useState(false);
  const [hasStartedExploring, setHasStartedExploring] = useState(false);
  const [settledInitialImageKeys, setSettledInitialImageKeys] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const userScrollIntentRef = useRef(false);
  const hasExpandedInitialBatchRef = useRef(false);
  const sourceImagesRef = useRef<typeof images | null>(null);

  useEffect(() => {
    if (sourceImagesRef.current === images) return;
    sourceImagesRef.current = images;
    setOrderedImages(shuffleWorkspacePreviewImages(images));
    setVisibleCount(WORKSPACE_PREVIEW_INITIAL_COUNT);
    setSettledInitialImageKeys(new Set());
    setHasStartedExploring(false);
    userScrollIntentRef.current = false;
    hasExpandedInitialBatchRef.current = false;
  }, [images]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      setLayout({ viewport: 'desktop', columnCount: 4 });
      return undefined;
    }

    const mobileMediaQuery = window.matchMedia(WORKSPACE_MOBILE_MEDIA_QUERY);
    const wideMediaQuery = window.matchMedia(
      WORKSPACE_PREVIEW_WIDE_MEDIA_QUERY
    );
    const update = () => {
      setLayout(
        mobileMediaQuery.matches
          ? { viewport: 'mobile', columnCount: 3 }
          : {
              viewport: 'desktop',
              columnCount: wideMediaQuery.matches ? 4 : 3,
            }
      );
    };
    update();

    mobileMediaQuery.addEventListener('change', update);
    wideMediaQuery.addEventListener('change', update);
    return () => {
      mobileMediaQuery.removeEventListener('change', update);
      wideMediaQuery.removeEventListener('change', update);
    };
  }, []);

  useEffect(() => {
    if (layout.viewport !== 'desktop') return;
    return runWhenPageReadyAndIdle(() => setCanLoadImages(true));
  }, [layout.viewport]);

  const visibleImages = orderedImages.slice(0, visibleCount);
  const hasMore = visibleCount < orderedImages.length;
  const columnCount = orderedImages.length === 1 ? 1 : layout.columnCount;
  const initialBatchSize = Math.min(
    WORKSPACE_PREVIEW_INITIAL_COUNT,
    orderedImages.length
  );
  const initialImageSources = useMemo(
    () =>
      new Set(
        orderedImages.slice(0, initialBatchSize).map((image) => image.src)
      ),
    [initialBatchSize, orderedImages]
  );
  const isInitialBatchReady = settledInitialImageKeys.size >= initialBatchSize;
  const handleImageSettled = useCallback(
    (image: CmsMasonryImageItem) => {
      setSettledInitialImageKeys((current) => {
        if (
          current.size >= initialBatchSize ||
          current.has(image.key) ||
          !initialImageSources.has(image.src)
        ) {
          return current;
        }

        const next = new Set(current);
        next.add(image.key);
        return next;
      });
    },
    [initialBatchSize, initialImageSources]
  );

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const scrollRoot = wrapper?.closest(WORKSPACE_CANVAS_SCROLL_ROOT_SELECTOR);
    if (layout.viewport !== 'desktop' || !wrapper || !scrollRoot) {
      return undefined;
    }

    const beginExploring = () => {
      if (hasExpandedInitialBatchRef.current) return;
      hasExpandedInitialBatchRef.current = true;
      userScrollIntentRef.current = true;
      setHasStartedExploring(true);
      setVisibleCount((current) =>
        Math.min(
          Math.max(
            current,
            WORKSPACE_PREVIEW_INITIAL_COUNT + WORKSPACE_PREVIEW_BATCH_SIZE
          ),
          orderedImages.length
        )
      );
    };
    const handleScroll = () => {
      const fadeDistance = scrollRoot.clientHeight * 0.15;
      const opacity =
        fadeDistance > 0
          ? 1 - Math.min(1, Math.max(0, scrollRoot.scrollTop) / fadeDistance)
          : 1;
      wrapper.style.setProperty(
        '--workspace-preview-mask-opacity',
        String(opacity)
      );
      if (scrollRoot.scrollTop > 0) beginExploring();
    };
    scrollRoot.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      scrollRoot.removeEventListener('scroll', handleScroll);
    };
  }, [layout.viewport, orderedImages.length]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const sentinel = sentinelRef.current;
    const scrollRoot = wrapper?.closest(WORKSPACE_CANVAS_SCROLL_ROOT_SELECTOR);
    if (
      layout.viewport !== 'desktop' ||
      !isInitialBatchReady ||
      !hasMore ||
      !scrollRoot ||
      !sentinel ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          !userScrollIntentRef.current ||
          scrollRoot.scrollTop <= 0 ||
          !entries.some((entry) => entry.isIntersecting)
        ) {
          return;
        }

        observer.disconnect();
        setVisibleCount((current) =>
          Math.min(current + WORKSPACE_PREVIEW_BATCH_SIZE, orderedImages.length)
        );
      },
      { root: scrollRoot }
    );
    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [
    hasMore,
    isInitialBatchReady,
    layout.viewport,
    orderedImages.length,
    visibleCount,
  ]);

  if (layout.viewport === 'mobile') return null;

  const hasSinglePreviewImage = images.length === 1;
  const imageSizes = resolveWorkspacePreviewImageSizes(images.length);
  return (
    <div
      ref={wrapperRef}
      className={cn(
        WORKSPACE_PREVIEW_MASONRY_WRAP_CLASS,
        hasSinglePreviewImage && WORKSPACE_PREVIEW_MASONRY_SINGLE_CLASS
      )}
      data-workspace-preview-exploring={hasStartedExploring}
    >
      {layout.viewport === 'pending' || !canLoadImages ? (
        <WorkspacePreviewSkeletonGrid
          images={visibleImages}
          columnCount={columnCount}
        />
      ) : (
        <WorkspacePreviewImageGrid
          images={visibleImages}
          columnCount={columnCount}
          sizes={imageSizes}
          onImageSettled={handleImageSettled}
        />
      )}
      {layout.viewport === 'desktop' && hasMore ? (
        <div
          ref={sentinelRef}
          aria-hidden="true"
          className="cms-block-workspace-workspace-1-workspace-showcase-layout__workspacePreviewMasonrySentinel"
          data-workspace-preview-sentinel=""
        />
      ) : null}
    </div>
  );
}

export function WorkspacePreviewMasonry({
  images,
}: {
  images: CmsWorkspaceShellModel['hero']['previewImages'];
}) {
  if (images.length === 0) return null;

  const imageListKey = images
    .map(
      (image) =>
        `${image.src}:${image.dimensions.width}x${image.dimensions.height}`
    )
    .join('|');

  return <WorkspacePreviewMasonryList key={imageListKey} images={images} />;
}
